import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, FileText, Link2 } from "lucide-react";
import { api, fileUrl } from "@/lib/apiClient";
import { Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Block({ b }) {
  if (b.type === "richtext") return <div className="prose prose-sm sm:prose max-w-none my-3" data-testid="content-richtext" dangerouslySetInnerHTML={{ __html: b.value || "" }} />;
  if (b.type === "image") return <img src={b.value?.startsWith("http") ? b.value : fileUrl(b.value)} alt="" className="rounded-xl w-full my-4 border border-border" data-testid="content-image" />;
  if (b.type === "link") return (
    <a href={b.value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-teal font-medium my-2 hover:underline">
      <Link2 className="w-4 h-4" /> {b.label || b.value} <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
  return <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap my-3">{b.value}</p>;
}

export default function Lesson() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/lessons/${lessonId}`);
    setLesson(data);
    const sib = await api.get(`/phases/${data.phase_id}/lessons`);
    setSiblings(sib.data);
  };
  useEffect(() => { setLesson(null); load(); /* eslint-disable-next-line */ }, [lessonId]);

  if (!lesson) return <Loader />;
  const idx = siblings.findIndex((l) => l.id === lessonId);
  const prev = siblings[idx - 1], next = siblings[idx + 1];

  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/lessons/${lessonId}/complete`);
      toast.success("Lesson completed!");
      setLesson((l) => ({ ...l, completed: true }));
      if (next) nav(`/lesson/${next.id}`);
      else nav("/course");
    } catch { toast.error("Could not save progress"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Button variant="ghost" className="rounded-full mb-4 -ml-2" onClick={() => nav("/course")} data-testid="back-to-course"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Course</Button>
      <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-10">
        <p className="text-sm text-teal font-semibold">Lesson {idx + 1} of {siblings.length}</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-1">{lesson.title}</h1>
        {lesson.description && <p className="text-muted-foreground mt-2">{lesson.description}</p>}
        <div className="mt-6">
          {(lesson.content_blocks || []).map((b, i) => <Block key={i} b={b} />)}
          {(!lesson.content_blocks || lesson.content_blocks.length === 0) && <p className="text-muted-foreground">Content for this lesson is coming soon.</p>}
        </div>
        {lesson.resources?.length > 0 && (
          <div className="mt-6 p-4 rounded-xl bg-secondary/50">
            <p className="font-medium text-sm mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4 text-teal" /> Resources</p>
            {lesson.resources.map((r, i) => <a key={i} href={r.url} target="_blank" rel="noreferrer" className="block text-teal text-sm hover:underline">{r.label || r.url}</a>)}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mt-6">
        <Button variant="outline" className="rounded-full" disabled={!prev} onClick={() => prev && nav(`/lesson/${prev.id}`)} data-testid="prev-lesson">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Previous
        </Button>
        <Button className="rounded-full bg-brandgreen hover:bg-brandgreen/90" onClick={complete} disabled={busy} data-testid="complete-lesson">
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> {lesson.completed ? (next ? "Next Lesson" : "Finish") : "Complete Lesson"}
        </Button>
        <Button variant="outline" className="rounded-full" disabled={!next} onClick={() => next && nav(`/lesson/${next.id}`)} data-testid="next-lesson">
          Next <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
